import styled from 'styled-components';
// components
import StarIcon from 'components/atoms/icons/Star';
import ClassCover from 'components/atoms/photos/ClassCover';
// hooks
import useCommonBridge from 'hooks/bridge/useCommonBridge';
import { useSelector } from 'hooks/common/useStore';
// utils
import { formatToPrice } from 'lib/utils/format/number';
// styles
import * as typography from 'cds/typography';
import { palette, mediaQuery } from 'cds/styles';

/**
 * 클래스 아이템 row 타입 카드
 * @param {object} props
 * @param {import('types/slices/main').RecommendClass} props.data 클래스 정보
 * @param {boolean} props.showSalePer 할인율 노출 여부
 * @param {boolean} props.showPrice 가격 노출 여부
 */
const RowItem = ({ data, showSalePer, showPrice }) => {
  const { goToClassDetail, goToProfile } = useCommonBridge();
  const userInfo = useSelector(state => state.user.info);

  const onProfile = e => {
    e.stopPropagation();
    goToProfile(data.coach_id);
  };

  const onClass = () => {
    if (data.link) {
      window.location.href = data.link;
      return;
    }

    if (!userInfo.id) {
      window.location.href = `${process.env.NEXT_PUBLIC_DEFAULT_HOST}/class/classDetail/${data.class_id}`;
      return;
    }

    return goToClassDetail(data.class_id);
  };

  return (
    <Item onClick={onClass}>
      <CoverWrapper>
        <ClassCover src={data.class_photo}></ClassCover>
      </CoverWrapper>

      <Info>
        <Title>{data.class_name}</Title>
        <Summary>
          <Coach onClick={onProfile}>{data.coach_name}</Coach>
          {data.curriculum_cnt && (
            <CurriculumCount>
              <span>{`${data.curriculum_cnt}강`}</span>
            </CurriculumCount>
          )}
        </Summary>

        {(showSalePer || showPrice) && (
          <Price>
            {showSalePer && !!parseInt(data.price_sale, 10) && (
              <SalePer>{Math.floor(data.price_sale)}%</SalePer>
            )}
            {showPrice && data.payment && (
              <SalePrice>
                <span>월</span>
                {formatToPrice(data.payment)}원
              </SalePrice>
            )}
          </Price>
        )}
        {!!(data.review_score || data.signup_cnt) && (
          <Status>
            {!!data.review_score && (
              <span>
                <StarIcon type="S" isActive />
                {Math.ceil(data.review_score * 10) / 10}
              </span>
            )}
            {!!data.signup_cnt && <span>{formatToPrice(data.signup_cnt)}명 수강</span>}
          </Status>
        )}
      </Info>
    </Item>
  );
};

const Item = styled.a`
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  border-radius: 8px;
`;

const CoverWrapper = styled.div`
  flex: 1;
  width: 40%;
  max-width: 40%;
`;

const Info = styled.div`
  flex: 1;
  margin-left: 12px;

  & > * {
    margin-bottom: 4px;
  }

  ${mediaQuery.large} {
    width: auto;
  }
`;

const Title = styled.p`
  ${typography.subtitle4_medium}
  color: ${palette.grey75};
  word-break: keep-all;

  ${mediaQuery.large} {
    font-size: 15px;
  }
`;

const Summary = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  ${typography.caption3_regular}
  color: ${palette.grey0};

  ${mediaQuery.large} {
    ${typography.caption2_regular}
  }
`;

const Coach = styled.span`
  display: block;
`;

const CurriculumCount = styled.span`
  flex: 0 0 auto;

  &::before {
    content: '·';
    margin: 0 4px;
  }
`;

const Price = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 4px;
`;

const SalePer = styled.span`
  flex: 0 auto;
  margin-top: 2px;
  margin-right: 6px;
  ${typography.subtitle3_bold};
  color: ${palette.red060};
  font-weight: 500;

  ${mediaQuery.large} {
    font-size: 16px;
    font-weight: 500;
    color: ${palette.grey080};
  }
`;

const SalePrice = styled.span`
  ${typography.subtitle3_bold};
  color: ${palette.grey75};

  & > span {
    margin-right: 4px;
    font-size: 16px;
  }

  ${mediaQuery.large} {
    font-size: 18px;
    color: ${palette.red060};
  }
`;

const Status = styled.div`
  display: flex;
  align-items: center;
  ${typography.caption3_regular}
  color: ${palette.grey0};

  & span {
    display: flex;
    align-items: center;
    flex: 0 auto;

    svg {
      margin-right: 2px;
    }
  }

  & span ~ span::before {
    display: inline-block;
    content: '';
    width: 1px;
    margin: 0 4px;
    height: 8px;
    background: ${palette.grey040};
  }
`;
export default RowItem;
